import { useState, useEffect } from "react";

interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  decided: boolean;
}

const STORAGE_KEY = "thv_cookie_consent";

const loadConsent = (): ConsentState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveConsent = (state: ConsentState) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = loadConsent();
    if (!existing?.decided) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = (all: boolean) => {
    saveConsent({ analytics: all, marketing: all, decided: true });
    setVisible(false);
  };

  const saveCustom = () => {
    saveConsent({ analytics, marketing, decided: true });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center pointer-events-none px-4 pb-4 md:items-end md:justify-end md:pb-6 md:pr-6">
      <div
        className={`pointer-events-auto w-full max-w-md bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all duration-500 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
        style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.12)" }}
      >
        {/* Header */}
        <div className="bg-slate-900 px-5 py-4 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-400 flex-shrink-0">
            <i className="ri-cookie-line text-slate-900 text-base"></i>
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-none">Cookie-Einstellungen</p>
            <p className="text-slate-400 text-xs mt-0.5">German Traumhaus Lotterie · DSGVO</p>
          </div>
        </div>

        <div className="px-5 py-4">
          <p className="text-slate-600 text-sm leading-relaxed mb-4">
            Wir verwenden Cookies, um Ihr Erlebnis zu verbessern und unsere Besucherzahlen zu analysieren.
            Sie können Ihre Präferenzen anpassen oder alles akzeptieren.
          </p>

          {/* Detail panel */}
          {showDetails && (
            <div className="space-y-3 mb-4 border border-slate-100 rounded-xl p-4 bg-slate-50">
              {/* Essential — always on */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Essenzielle Cookies</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Sitzung, Sicherheit, Präferenzen. Notwendig für das Funktionieren der Website.</p>
                </div>
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-10 h-5 bg-secondary-light rounded-full flex items-center justify-end px-0.5 cursor-not-allowed" title="Immer aktiv">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                  <p className="text-[10px] text-secondary-dark font-semibold text-center mt-0.5">Erforderlich</p>
                </div>
              </div>

              <div className="h-px bg-slate-200"></div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Analyse-Cookies</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Google Analytics — anonymisierte Besuchermessung zur Verbesserung der Website.</p>
                </div>
                <button
                  onClick={() => setAnalytics((v) => !v)}
                  className={`flex-shrink-0 mt-0.5 w-10 h-5 rounded-full flex items-center transition-all duration-200 cursor-pointer px-0.5 ${
                    analytics ? "bg-yellow-400 justify-end" : "bg-slate-300 justify-start"
                  }`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>

              <div className="h-px bg-slate-200"></div>

              {/* Marketing */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Marketing-Cookies</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Facebook-Pixel, personalisierte Werbung und Conversion-Tracking.</p>
                </div>
                <button
                  onClick={() => setMarketing((v) => !v)}
                  className={`flex-shrink-0 mt-0.5 w-10 h-5 rounded-full flex items-center transition-all duration-200 cursor-pointer px-0.5 ${
                    marketing ? "bg-yellow-400 justify-end" : "bg-slate-300 justify-start"
                  }`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>
            </div>
          )}

          {/* Toggle details */}
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors cursor-pointer mb-4 w-full"
          >
            <i className={`${showDetails ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base`}></i>
            {showDetails ? "Details ausblenden" : "Einstellungen anpassen"}
          </button>

          {/* Buttons */}
          <div className="flex flex-col gap-2">
            {showDetails ? (
              <>
                <button
                  onClick={saveCustom}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Auswahl speichern
                </button>
                <button
                  onClick={() => accept(true)}
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Alle akzeptieren
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => accept(true)}
                  className="w-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Alle akzeptieren
                </button>
                <button
                  onClick={() => accept(false)}
                  className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Nur essenzielle akzeptieren
                </button>
              </>
            )}
          </div>

          <p className="text-[11px] text-slate-400 text-center mt-3 leading-relaxed">
            Mehr erfahren Sie in unserer{" "}
            <a href="/mentions-legales" className="underline hover:text-slate-600 cursor-pointer">
              Datenschutzerklärung
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
